import React, { useState, useEffect, useMemo } from 'react';
import {
    E,
    T,
    usePortfolio,
    useQuotes,
    wsClient,
    Order,
    Position
} from '../../logic/imports';
import * as Common from '../app/CommonComps';
import { ConfirmOrderModal, ModalAction } from './ConfirmOrderModal';
import { isOrderManaged } from '../../shared/utils';
import { showDimmer } from '../app/Dimmer';


const DEBUG_INPUTS = false; // Toggle for input validation logs
const USE_DEBUG_CONTRACT = false; // Toggle to use a debug contract quote (for when market closed and can't get real one)

interface OrderDetailsProps {
    position: Position;
    side: E.OrderSide;
    type: E.OrderType;
    onClose: (hasEdits: boolean, errorMsg?: any) => void; // Callback, to close this component
    existingOrder?: Order; // If provided, pre-populate fields for editing
    onSubmitEdit?: (order: Order) => void; // Callback after successfully submitting edited order
}

export const OrderDetails: React.FC<OrderDetailsProps> = (props) => {
    const { position, existingOrder } = props;
    const { balances } = usePortfolio();
    const [pendingAction, setPendingAction] = useState<ModalAction | null>(null);

    // Get account data
    const buyingPower = balances?.cashSettled ?? 0;
    const instrument = position.option;

    // Initialize quotes states
    const { quotes, requestQuote, releaseQuote } = useQuotes();
    const [stockPrice, setStockPrice] = useState<number | undefined>();
    const [contract, setContract] = useState<T.Quote | undefined>();
    const [percentGain, setPercentGain] = useState<number>(0);

    // Initialize order state from existingOrder if provided, else null
    const [quantity, setQuantity] = useState<number | undefined>(existingOrder?.quantity ?? undefined);
    const [priceTP, setPriceTP] = useState<number | undefined>(existingOrder?.takeProfit?.value ?? undefined);
    const [priceSL, setPriceSL] = useState<number | undefined>(existingOrder?.stopLoss?.value ?? undefined);
    const [priceTD, setPriceTD] = useState<number | undefined>(existingOrder?.trailDistance ?? undefined);
    const [priceRef, setPriceRef] = useState<E.PriceReference>(
        existingOrder?.takeProfit?.reference ?? existingOrder?.stopLoss?.reference ?? E.PriceReference.CONTRACT
    );
    const [inputsValid, setInputsValid] = useState<boolean>(false);

    //#region Effect Hooks

    // Subscribe to quotes on mount, unsubscribe on unmount
    // Empty dependencies so this runs once per component lifetime
    useEffect(() => {
        const stockSymbol = instrument.underlying!.symbol;
        const subId = crypto.randomUUID().substring(0, 3);
        
        requestQuote(stockSymbol, `OrderDetails-${subId}`);
        requestQuote(instrument.symbol, `OrderDetails-${subId}`);

        return () => {
            releaseQuote(stockSymbol, `OrderDetails-${subId}`);
            releaseQuote(instrument.symbol, `OrderDetails-${subId}`);
        };
    }, []);

    // Update local quotes and percent gain whenever quoteCtx has changes
    // This just reads from the quotes array, doesn't call requestQuote
    useEffect(() => {
        const stockSymbol = instrument.underlying!.symbol;
        const stockQ = quotes.find(q => q.instrument.symbol === stockSymbol);
        if (stockQ) setStockPrice(stockQ.last);

        const contractQ = quotes.find(q => q.instrument.symbol === instrument.symbol);
        if (contractQ) setContract(contractQ);
        else if (USE_DEBUG_CONTRACT && process.env.NODE_ENV === 'development') {
            console.warn('[OrderDetails] Using debug contract quote');

            const testCon: T.Quote = {
                instrument: {
                    symbol: 'TEST',
                    name: 'Test Contract',
                    type: E.OptionType.CALL,
                    expiration: '2026-12-31',
                    strike: 560,
                    underlying: {
                        symbol: 'SPY',
                        name: 'SPDR S&P 500 ETF Trust',
                        kind: 'stock' as const,
                    },
                    kind: 'option' as const,
                },
                last: 1.2,
                bid: 1.2,
                ask: 1.1,
                volume: 1000,
                bidSize: 10,
                askSize: 12,
                timestamp: new Date().toISOString(),
                openInterest: 500
            }
            
            setContract(testCon);
        }

        // Update percent gain based on position
        if (position?.stats && contract) {
            const currentValue = contract.last * 100 * position.contractsHeld;
            setPercentGain((currentValue / position.stats.totalCost - 1) * 100);
        }
    }, [quotes]);

    // Reset priceRef whenever the panel type changes (panel switched)
    // or initialize from existingOrder on mount
    useEffect(() => {
        if (existingOrder) {
            setQuantity(existingOrder.quantity ?? undefined);
            setPriceTP(existingOrder.takeProfit?.value ?? undefined);
            setPriceSL(existingOrder.stopLoss?.value ?? undefined);
            setPriceTD(existingOrder.trailDistance ?? undefined);
            setPriceRef(existingOrder.takeProfit?.reference ?? existingOrder.stopLoss?.reference ?? E.PriceReference.CONTRACT);
        } else {
            setPriceRef(E.PriceReference.CONTRACT);
        }
    }, [props.type, existingOrder]);

    // Check inputs validity whenever relevant state changes
    useEffect(() => {
        checkIfInputsValid();   
    }, [props.type, quantity, priceTP, priceSL, priceTD, priceRef]);

    // Reset price values when reference is MANUALLY changed (only if NOT editing an existing order)
    useEffect(() => {
        // Don't clear prices if we're editing an existing order, as they were just loaded
        if (!existingOrder) {
            setPriceTP(undefined);
            setPriceSL(undefined);
            setPriceTD(undefined);
        }
    }, [priceRef, existingOrder]);

    /** Calculates number of contracts that can be purchased with available BP. */
    const affordableContracts = useMemo(() => {
        if (!buyingPower || !contract)
            return 0;

        const contractCost = contract.ask! * 100;
        return Math.floor(buyingPower / contractCost);
    }, [buyingPower, contract]);

    /** Calculate the initial stop price for managed (FLEX) orders */
    const initialTrailingStop = useMemo(() => {
        if (props.type !== E.OrderType.FLEX || !priceTD)
            return undefined;
        
        if (priceRef === E.PriceReference.CONTRACT && contract) {
            return contract.last - priceTD;
        } else if (priceRef === E.PriceReference.STOCK && stockPrice) {
            // for trailing by stock price, adjust what side of price we trail on based on call/put
            const modifier = instrument.type === E.OptionType.CALL ? 1 : -1;
            return stockPrice - priceTD * modifier;
        } else if (priceRef === E.PriceReference.PERCENT && contract) {
            return percentGain - priceTD;
        }
        return undefined;
    }, [priceTD, priceRef, contract, stockPrice]);

    //#endregion

    //#region Helpers

    /** Ensures edits to the order are detected */
    const differentThanExisting = (): boolean => {
        // No existing order = always different (allow submit)
        if (!existingOrder)
            return true;
        
        // Check if ANY field differs from existing order
        const quantityChanged = quantity !== existingOrder.quantity;
        const existingRef = existingOrder.takeProfit?.reference ?? existingOrder.stopLoss?.reference;
        const refChanged = priceRef !== existingRef;
        const tpChanged = priceTP !== (existingOrder.takeProfit?.value ?? undefined);
        const slChanged = priceSL !== (existingOrder.stopLoss?.value ?? undefined);
        const tdChanged = priceTD !== (existingOrder.trailDistance ?? undefined);
        
        const isDifferent = quantityChanged || refChanged || tpChanged || slChanged || tdChanged;
        // console.log('[OrderDetails] Checking if inputs differ from existing order:', {
        //     priceTP, existingLimitPrice: existingOrder.limitPrice
        // });
        if (DEBUG_INPUTS && !isDifferent) {
            console.log('[NewOrderDetails] Form unchanged from existing order');
        }
        
        return isDifferent;
    };

    const checkIfInputsValid = () => {
        // Basic checks //
        // No quantity value
        if (!quantity || quantity <= 0) {
            DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid: No quantity or quantity <= 0');
            setInputsValid(false);
            return;
        }

        // Managed (FLEX) checks //
        if (props.type === E.OrderType.FLEX) {
            // If trailing distance provided, validate trailing inputs
            if (priceTD !== undefined) {
                if (priceTD <= 0) {
                    DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid FLEX: priceTD is <= 0');
                    setInputsValid(false);
                    return;
                }
                setInputsValid(true);
                return;
            }

            // Otherwise validate bracket-style SL/TP inputs
            if (!priceSL || !priceTP) {
                DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid FLEX: SL or TP is null');
                setInputsValid(false);
                return;
            }
            // SL/TP must be </> current percent gain
            else if (priceRef === E.PriceReference.PERCENT) {
                const slValid = (priceSL < percentGain && priceSL > -100);
                const tpValid = (priceTP >= percentGain);
                if (!slValid) DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/PERCENT: SL outside [-100, currentPercentGain)');
                if (!tpValid) DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/PERCENT: TP < currentPercentGain');
                setInputsValid(slValid && tpValid);
                return;
            }
            // SL/TP must be </> current stock price
            else if (priceRef === E.PriceReference.STOCK) {
                if (!stockPrice) { 
                    DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/STOCK: stockPrice not a number');
                    setInputsValid(false); 
                    return; 
                }
                
                let slValid: boolean;
                let tpValid: boolean;
                
                if (isPut()) {
                    // PUT: TP < stock, SL > stock
                    slValid = priceSL > stockPrice;
                    tpValid = priceTP < stockPrice;
                    if (!slValid) DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/STOCK (PUT): SL <= stockPrice');
                    if (!tpValid) DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/STOCK (PUT): TP >= stockPrice');
                } else {
                    // CALL: TP > stock, SL < stock
                    slValid = priceSL < stockPrice;
                    tpValid = priceTP > stockPrice;
                    if (!slValid) DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/STOCK: SL >= stockPrice');
                    if (!tpValid) DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/STOCK: TP <= stockPrice');
                }
                
                setInputsValid(slValid && tpValid);
                return;
            }
            // SL/TP must be </> current contract price
            else if (priceRef === E.PriceReference.CONTRACT) {
                if (!contract) { 
                    DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/CONTRACT: contract not loaded');
                    setInputsValid(false); 
                    return; 
                }
                const slValid = priceSL < contract.bid;
                const tpValid = priceTP > contract.ask;
                if (!slValid) DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/CONTRACT: SL >= contract.bid');
                if (!tpValid) DEBUG_INPUTS && console.log('[NewOrderDetails] Invalid BRACKET/CONTRACT: TP <= contract.ask');
                setInputsValid(slValid && tpValid);
                return;
            }
        }

        // Market checks
        if (props.type === E.OrderType.MARKET) {
            setInputsValid(true);
            return;
        }

        DEBUG_INPUTS && console.warn('[NewOrderDetails] Invalid: No valid order type handler or order type not supported');
        setInputsValid(false);
    }

    //#region Helper functions for bracket orders

    /**
     * Determine if we're dealing with a PUT option (for side-aware TP/SL constraints)
     */
    const isPut = () => instrument.type === E.OptionType.PUT;

    /**
     * For STOCK reference: TP and SL constraints depend on option type
     * CALL/LONG: TP > stock (profit if up), SL < stock (loss if down)
     * PUT/SHORT: TP < stock (profit if down), SL > stock (loss if up)
     */
    const getStockRefConstraints = () => {
        if (isPut()) {
            // PUT option: TP < stock, SL > stock
            return {
                tpPlaceholder: `< ${stockPrice}`,
                slPlaceholder: `> ${stockPrice}`,
                tpMax: stockPrice ? Math.round((stockPrice - 0.01) * 100) / 100 : undefined,
                slMin: stockPrice ? Math.round((stockPrice + 0.01) * 100) / 100 : undefined
            };
        } else {
            // CALL option: TP > stock, SL < stock
            return {
                tpPlaceholder: `> ${stockPrice}`,
                slPlaceholder: `< ${stockPrice}`,
                tpMin: stockPrice ? Math.round((stockPrice + 0.01) * 100) / 100 : undefined,
                slMax: stockPrice ? Math.round((stockPrice - 0.01) * 100) / 100 : undefined
            };
        }
    };

    /**
     * Generate placeholder text for TP based on reference type
     */
    const getTpPlaceholder = () => {
        if (priceRef === E.PriceReference.PERCENT) return `> ${percentGain}`;
        if (priceRef === E.PriceReference.CONTRACT) return `> ${contract?.ask}`;
        if (priceRef === E.PriceReference.STOCK && stockPrice) {
            return getStockRefConstraints().tpPlaceholder;
        }
        return "";
    };

    /**
     * Generate placeholder text for SL based on reference type
     */
    const getSlPlaceholder = () => {
        if (priceRef === E.PriceReference.PERCENT) return `< ${percentGain}`;
        if (priceRef === E.PriceReference.CONTRACT) return `< ${contract?.bid}`;
        if (priceRef === E.PriceReference.STOCK && stockPrice) {
            return getStockRefConstraints().slPlaceholder;
        }
        return "";
    };

    /**
     * Generate min/max constraints for TP input
     */
    const getTpConstraints = () => {
        if (priceRef === E.PriceReference.PERCENT) {
            return { min: -99 };
        }
        if (priceRef === E.PriceReference.CONTRACT && contract) {
            return { min: Math.round((contract.ask + 0.01) * 100) / 100 };
        }
        if (priceRef === E.PriceReference.STOCK && stockPrice) {
            const constraints = getStockRefConstraints();
            return { min: constraints.tpMin, max: constraints.tpMax };
        }
        return { min: 0 };
    };

    /**
     * Generate min/max constraints for SL input
     */
    const getSlConstraints = () => {
        if (priceRef === E.PriceReference.PERCENT) {
            return { min: -99, max: percentGain - 1 };
        }
        if (priceRef === E.PriceReference.CONTRACT && contract) {
            return { max: Math.round((contract.bid - 0.01) * 100) / 100 };
        }
        if (priceRef === E.PriceReference.STOCK && stockPrice) {
            const constraints = getStockRefConstraints();
            return { min: constraints.slMin, max: constraints.slMax };
        }
        return { min: 0 };
    };

    //#endregion

    const generateContractsField = () => {
        let max = position.contractsAvailable;

        if (existingOrder && props.side === E.OrderSide.SELL) {
            max += existingOrder.quantity;
        }
        
        return (
            <Common.KeyValueInput
                name="Contracts"
                value={quantity !== undefined ? quantity : ''}
                placeholder={'Max: ' + max}
                onBlur={(e) => setQuantity(e === '' ? undefined : Number(e))}
                min={1}
                max={max}
                inputStyleOverride={{
                    width: '100px'
                }}
            />
        )
    }

    const generateReferenceField = () => {
        const options = [
            // { value: E.PriceReference.NONE, label: 'Select One...' },
            { value: E.PriceReference.CONTRACT, label: `Contract` },
            { value: E.PriceReference.PERCENT, label: `Percent` },
            { value: E.PriceReference.STOCK, label: `Stock` },
        ];

        return (
            <Common.KeyValueDropdown
                name="Price Reference"
                value={priceRef}
                options={options}
                onChange={(value) => setPriceRef(value as E.PriceReference)}
                selectStyleOverride={{
                    width: '100px',
                    color: "#d1dee7"
                }}
            />
        );
    };

    const onSubmitOrder = async () => {
        if (existingOrder) {
            const oldStatus = existingOrder.status;
            existingOrder.status = E.OrderStatus.PENDING_MODIFICATION;

            // Edit mode - submit updated order
            const updatedOrder: any = {
                ...existingOrder,
                quantity: quantity ?? existingOrder.quantity,
                takeProfit: priceTP !== undefined ? { value: priceTP, reference: priceRef } : undefined,
                stopLoss: priceSL !== undefined ? { value: priceSL, reference: priceRef } : undefined,
                trailDistance: priceTD ?? undefined
            };
            
            // For managed orders, set the initial stop as the stopPrice when trailing distance is provided
            if (existingOrder.type === E.OrderType.FLEX && initialTrailingStop !== undefined) {
                updatedOrder.stopLoss = { value: initialTrailingStop, reference: priceRef };
            }
            
            // console.log('[OrderDetails] Submitting edited order:', updatedOrder);
            try {
                let r = await wsClient.sendRequest(
                    E.WsRoute.ORDERS,
                    E.WsOrdersCommand.MODIFY,
                    updatedOrder
                );
                console.log('[OrderDetails] Order updated successfully');
                // WebSocket push message will update the order in ordersManager
                props.onClose(true);
            } catch (error: any) {
                console.error('[OrderDetails] Order update failed. Error:', error);
                showDimmer('Could not update order: ' + error.message, true);
                existingOrder.status = oldStatus;
            }
        } else {
            // Create mode - new order submission
            if (!inputsValid || !quantity) {
                console.warn('[OrderDetails] Cannot submit order: Invalid inputs');
                return;
            }

            const order: any = {
                id: crypto.randomUUID(),
                brokerageId: undefined,
                instrument: instrument,
                status: E.OrderStatus.WORKING,
                side: props.side,
                type: props.type,
                quantity: quantity,
                takeProfit: priceTP !== undefined ? { value: priceTP, reference: priceRef } : undefined,
                stopLoss: priceSL !== undefined ? { value: priceSL, reference: priceRef } : undefined,
                trailDistance: priceTD ?? undefined,
                isManaged: isOrderManaged(props.type, props.side, priceRef),
                timeInForce: E.TimeInForce.DAY
            };
            
            // For managed orders, set the initial stop as the stopPrice when trailing distance is provided
            if (props.type === E.OrderType.FLEX && initialTrailingStop !== undefined) {
                order.stopLoss = { value: initialTrailingStop, reference: priceRef };
            }
            
            try {
                await wsClient.sendRequest(
                    E.WsRoute.ORDERS,
                    E.WsOrdersCommand.PLACE,
                    order
                );
                // console.log('[OrderDetails] New order submitted successfully');
                // WebSocket push message will update the order in ordersManager
                // notify parent that order was placed successfully
                props.onClose(true);
            } catch (error: any) {
                showDimmer('Could not place order: ' + error.message, true);
                props.onClose(false, error.message);
            }
            return;
        }
    }

    /** Function assigned to pendingAction.onConfirm() by Update button */
    const handleUpdateOrder = async () => {
        await onSubmitOrder();
    }

    /**
     * Callback triggered by ConfirmOrderModal's onConfirm(), which in turn
     * triggers the currently assigned pendingAction's onConfirm() function
     * */
    const handleModalConfirm = async () => {
        if (!pendingAction) return;
        try {
            await pendingAction.onConfirm();
        } catch (error) {
            console.error('[OrderDetails] Modal action failed:', error);
        } finally {
            setPendingAction(null);
        }
    }

    //#endregion

    //#region TSX Templates

    /** Displays before quote information has been loaded */
    const loadingTsx = (
        <div className='orderDetails--loading'
            style={{
                borderTop: existingOrder ? 'none' : '2px solid #b47500',
            }}>
            {!existingOrder && (
                <div className='orderDetails--new-header'>
                    {props.type} {props.side}
                </div>
            )}

            <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Loading prices...</p>
            </div>
        </div>
    );

    /**
     *** PRE-LOADED RENDER STATE ***
     * 
     * Check if quote data is still loading, and if so, show loading state
     * We do this here so that the rest of the component can assume these
     * values are defined and not have to check for them
     */
    if (!contract || !stockPrice) {
        return loadingTsx;
    }

    const costOrProfitValue = quantity && contract ? 
        (quantity * (props.side === E.OrderSide.BUY ? contract.ask : contract.bid) * 100) : undefined;


    /** Generates the TSX for the trailing order type */
    const trailingTsx = () => {
        return (<div>
            <div>
                {generateContractsField()}

                {/* Reference */}
                {generateReferenceField()}
                
                {/* Trail Distance */}
                <Common.KeyValueInput
                    name={`Trail Distance (${
                        priceRef === E.PriceReference.PERCENT ? '%' : '$'
                    })`}
                    value={priceTD !== undefined ? priceTD : ''}
                    placeholder={
                        priceRef === E.PriceReference.PERCENT ? '>= 1' : '>= 0.01'
                    }
                    onBlur={(e) => setPriceTD(e === '' ? undefined : Number(e))}
                    min={ priceRef === E.PriceReference.PERCENT ? 1 : 0.01 }
                    inputStyleOverride={{
                        width: '100px'
                    }}
                />

                {/* Last Price*/}
                <Common.KeyValueDisplay
                    name="Current Price"
                    value={
                        priceRef === E.PriceReference.CONTRACT ? contract.last :
                        priceRef === E.PriceReference.STOCK ? stockPrice :
                        priceRef === E.PriceReference.PERCENT ? percentGain :
                        0
                    }
                    keyStyleOverride={{ color: '#b0b0b0ff' }}
                    valueStyleOverride={{
                        color: '#b0b0b0ff',
                        width: '100px',
                        textAlign: 'center'
                    }}
                    numericValueFormat={
                        priceRef === E.PriceReference.PERCENT ? 'percentage' : 'dollars'
                    }
                />

                {/* Initial Stop display */}
                <Common.KeyValueDisplay
                    name="Initial Stop"
                    value={initialTrailingStop ?? '...'}
                    keyStyleOverride={{ color: '#b0b0b0ff' }}
                    valueStyleOverride={{
                        color: '#b0b0b0ff',
                        width: '100px',
                        textAlign: 'center'
                    }}
                    numericValueFormat={
                        initialTrailingStop ?
                        priceRef === E.PriceReference.PERCENT ? 'percentage' : 'dollars' :
                        undefined
                    }
                />
            </div>
        </div>)
    }

    /** Generates the TSX for the bracket order type */
    const flexTsx = (
        <div>
            <div>
                {/* Contracts */}
                {generateContractsField()}

                {/* Reference */}
                {generateReferenceField()}

                {/* Targets Side-by-Side */}
                <div style={{
                    width: 'fit-content',
                    gap: '50px',
                    display: 'flex',
                    margin: '0 auto',
                }}>
                    {/* Stop-Loss */}
                    <Common.KeyValueInput
                        name="Stop-Loss"
                        value={priceSL ? priceSL : ''}
                        placeholder={getSlPlaceholder()}
                        onBlur={(e) => setPriceSL(e === '' ? undefined : Number(e))}
                        {...getSlConstraints()}
                        inputStyleOverride={{
                            width: '100px'
                        }}
                    />

                    {/* Take-Profit */}
                    <Common.KeyValueInput
                        name="Take-Profit"
                        value={priceTP ? priceTP : ''}
                        placeholder={getTpPlaceholder()}
                        onBlur={(e) => setPriceTP(e === '' ? undefined : Number(e))}
                        {...getTpConstraints()}
                        inputStyleOverride={{
                            width: '100px'
                        }}
                    />
                </div>

                {/* Bid or Ask Price*/}
                <Common.KeyValueDisplay
                    name={props.side === E.OrderSide.BUY ? "Ask" : "Bid"}
                    value={(props.side === E.OrderSide.BUY ? contract.ask : contract.bid)}
                    keyStyleOverride={{ color: '#b0b0b0ff' }}
                    valueStyleOverride={{ color: '#b0b0b0ff' }}
                    numericValueFormat={
                        (contract.ask && contract.bid) ? 'dollars' : undefined
                    }
                />

            </div>
        </div>
    );

    /** Generates the TSX for the market order type */
    const marketTsx = (
        <div>
            {generateContractsField()}

            {/* Cost/Profit Display*/}
            <Common.KeyValueDisplay
                name={props.side === E.OrderSide.BUY ? "Estimated Cost" : "Estimated Proceeds"}
                value={costOrProfitValue ? costOrProfitValue : '...'}
                keyStyleOverride={{ color: '#b0b0b0ff' }}
                valueStyleOverride={{
                    color: '#b0b0b0ff',
                    width: '100px',
                    textAlign: 'center'
                }}
                numericValueFormat={
                    costOrProfitValue ? 'dollars' : undefined
                }
            />
        </div>
    );


    /**
     * Generates the TSX for editing orders in-line, underneath the given
     * order's position in the OrdersDisplay component.
     */
    const existingOrderTsx = (
        <div
            className='orderDetails--edit'
            style={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Order Details Entry */}
            <div>
                {/* Order type specific inputs and submit buttons */}
                {props.type === E.OrderType.FLEX && flexTsx}
                {props.type === E.OrderType.MARKET && marketTsx}
            </div>

            {/* Cancel & Update buttons */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-around',
                padding: '5px 0 5px 0'
            }}>
                {/* <button
                    className="button button--red"
                    onClick={() => setPendingAction({
                        message: "Are you sure you want to cancel this order?",
                        onConfirm: handleCancelOrder
                    })}
                >
                    Cancel
                </button> */}

                <button
                    className={`button button--green`}
                    onClick={() => setPendingAction({
                        message: "Are you sure you want to update this order?",
                        onConfirm: handleUpdateOrder
                    })}
                    disabled={!inputsValid || !differentThanExisting()}
                >
                    Update
                </button>
            </div>

            {/* Confirmation Modal */}
            <ConfirmOrderModal
                isOpen={pendingAction !== null}
                message={pendingAction?.message ?? ''}
                onConfirm={handleModalConfirm}
                onCancel={() => setPendingAction(null)}
            />
        </div>
    );

    /**
     * Generates the TSX for creating new orders,
     * popping up under the OrdersDisplay component.
     */
    const newOrderTsx = (
        <div className='orderDetails--new' style={{
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header - Type & Side */}
            <div className='orderDetails--new-header'>
                {props.type} {props.side}
            </div>

            {/* Order Details Entry */}
            <div>
                {/* Order type specific inputs and submit buttons */}
                {props.type === E.OrderType.FLEX && flexTsx}
                {props.type === E.OrderType.MARKET && marketTsx}
            </div>

            {/* Buttons */}
            <div style={{ 
                display: 'flex',
                justifyContent: 'space-around',
                padding: '5px 0 5px 0'
            }}>

                {/* Back */}
                <button
                    className="button"
                    onClick={() => props.onClose(false)}
                >
                    ←Back
                </button>

                {/* Buy/Sell */}
                {!existingOrder && (
                    <button
                        className={`button ` +
                            `${props.side === E.OrderSide.BUY ? 'button--green' : 'button--red'} `}
                        onClick={onSubmitOrder}
                        disabled={!inputsValid}
                    >
                        {props.side === E.OrderSide.BUY ? 'Buy' : 'Sell'}
                    </button>
                )}

            </div>
        </div>
    )

    /**
    *** LOADED RENDER STATE ***
    *
    * At this point, all quote data has been loaded and the component can render
    * the appropriate TSX based on whether we're editing an existing order
    * or creating a new one.
    */
    if (existingOrder) 
        return existingOrderTsx;
    return newOrderTsx;
}
